import React, { useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectItem } from "@/components/ui/simple-select";
import type { BaseEntity } from "../../types";
import {
  computePaginationInfo,
  isNextDisabled as computeIsNextDisabled,
  isPreviousDisabled as computeIsPreviousDisabled,
  resolveNextPage,
  resolvePreviousPage,
  shouldRenderPagination,
} from "./pagination-math";

export interface TablePaginationProps<TData extends BaseEntity> {
  totalPages: number;
  propTotalItems?: number;
  paginatedData: TData[];
  processedData: TData[];
  effectiveCurrentPage: number;
  effectivePageSize: number;
  effectiveSetCurrentPage: (page: number) => void;
  effectiveSetPageSize: (size: number) => void;
  propOnPageChange?: (page: number) => void;
  infiniteScrollEnabled?: boolean;
}

export function TablePagination<TData extends BaseEntity>({
  totalPages,
  propTotalItems,
  paginatedData,
  processedData,
  effectiveCurrentPage,
  effectivePageSize,
  effectiveSetCurrentPage,
  effectiveSetPageSize,
  propOnPageChange,
  infiniteScrollEnabled,
}: TablePaginationProps<TData>) {
  // Hide pagination when infinite scroll is enabled
  if (infiniteScrollEnabled) {
    return null;
  }

  // Memoize callbacks to prevent unnecessary re-renders
  const handlePrevious = useCallback(() => {
    const target = resolvePreviousPage(effectiveCurrentPage);
    if (propOnPageChange) {
      // For cursor-based pagination, use the external handler
      propOnPageChange(target);
    } else {
      // For client-side pagination, use internal handler
      effectiveSetCurrentPage(target);
    }
  }, [propOnPageChange, effectiveCurrentPage, effectiveSetCurrentPage]);

  const handleNext = useCallback(() => {
    if (propOnPageChange) {
      // For cursor-based pagination, use the external handler
      propOnPageChange(resolveNextPage(effectiveCurrentPage, totalPages, true));
    } else {
      // For client-side pagination, use internal handler
      effectiveSetCurrentPage(resolveNextPage(effectiveCurrentPage, totalPages, false));
    }
  }, [propOnPageChange, effectiveCurrentPage, effectiveSetCurrentPage, totalPages]);

  const handlePageSizeChange = useCallback(
    (value: string) => {
      effectiveSetPageSize(parseInt(value));
    },
    [effectiveSetPageSize]
  );

  // Memoize pagination display values
  const paginationInfo = useMemo(
    () =>
      computePaginationInfo({
        propTotalItems,
        currentPage: effectiveCurrentPage,
        pageSize: effectivePageSize,
        processedCount: processedData.length,
      }),
    [propTotalItems, effectiveCurrentPage, effectivePageSize, processedData.length]
  );

  // Memoize disabled states
  const isPreviousDisabled = useMemo(
    () => computeIsPreviousDisabled(effectiveCurrentPage),
    [effectiveCurrentPage]
  );
  const isNextDisabled = useMemo(
    () => computeIsNextDisabled(effectiveCurrentPage, totalPages),
    [effectiveCurrentPage, totalPages]
  );

  if (!shouldRenderPagination({ totalPages, propTotalItems })) {
    return null;
  }

  return (
    <div className="px-3 py-3 sm:px-4 border-t border-gray-200 dark:border-gray-700 bg-card-50">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Results count + page-size selector */}
        <div className="flex items-center justify-between gap-3 sm:gap-4">
          <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
            <span className="hidden sm:inline">Showing </span>
            <span className="font-medium">{paginationInfo.start}</span>
            <span className="px-0.5">–</span>
            <span className="font-medium">{paginationInfo.end}</span>
            <span className="hidden sm:inline"> of </span>
            <span className="sm:hidden"> / </span>
            <span className="font-medium">{paginationInfo.total}</span>
            <span className="hidden sm:inline"> results</span>
          </div>

          {/* Page Size Selector */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="hidden sm:inline text-sm text-gray-600 dark:text-gray-400">Show:</span>
            <Select
              value={effectivePageSize.toString()}
              onValueChange={handlePageSizeChange}
              className="w-16 sm:w-20"
            >
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
            </Select>
            <span className="hidden sm:inline text-sm text-gray-600 dark:text-gray-400">per page</span>
          </div>
        </div>

        {/* Pager — full-width with large tap targets on mobile, compact on desktop */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={isPreviousDisabled}
            className="h-11 flex-1 sm:h-8 sm:flex-none"
          >
            Previous
          </Button>
          <span className="px-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap text-center">
            <span className="hidden sm:inline">Page </span>
            {effectiveCurrentPage + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={isNextDisabled}
            className="h-11 flex-1 sm:h-8 sm:flex-none"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
